import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";

const MobileAppDevelopment = () => {
  const phrases = [
    "App Design  ",
    "App Development"  
  ];

  const cardData = [
    { type: 'purple-clay', content: '📱' },
    { type: 'smooth-wave', content: '⚙️' },
    { type: 'abstract-blocks', content: '🚀' },
    { type: 'pink-sphere', content: '💎' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );
    
    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const designServices = [
    { icon: "📱", title: "iOS App Development", desc: "Premium, robust native application experiences developed specifically for the Apple ecosystem." },
    { icon: "🤖", title: "Android App Development", desc: "Highly secure, powerful native Android software built to target diverse mobile device sizes." },
    { icon: "⚛️", title: "Cross-Platform Development", desc: "Deploy unified application build targets seamlessly across platform lines using React Native or Flutter." },
    { icon: "✨", title: "UI/UX Mobile Architecture", desc: "Intuitive, thumb-friendly native styling and visual layouts keeping user conversion rates high." },
    { icon: "🛒", title: "M-Commerce Solutions", desc: "Integrate specialized retail cart ecosystems, mobile checkout steps, and fast payment pathways." },
    { icon: "🔄", title: "Legacy App Transformation", desc: "Refactor dynamic systems, update outmoded features, and migrate legacy mobile code seamlessly." },
    { icon: "🏢", title: "Enterprise Mobile Tools", desc: "Build tailored internal applications to power field operations and scale internal workflows securely." }
  ];

  const devServices = [
    { icon: "🔗", title: "Custom Mobile App Development", desc: "Tailored custom logic targeting unique application workflows from initial scope to execution." },
    { icon: "⚙️", title: "Backend API Frameworks", desc: "Develop secure and fast restful communication loops linking front applications to backend storage." },
    { icon: "💳", title: "Payment System Integration", desc: "Deploy certified mobile payment checkouts including modern wallet architectures seamlessly." },
    { icon: "🔔", title: "Push Notification Systems", desc: "Configure smart alerts and targeted push scheduling architectures to scale customer return rates." },
    { icon: "🔐", title: "Rigorous Device Security", desc: "Ensure strong data protections utilizing device encryption layers and safe authentication steps." },
    { icon: "☁️", title: "Cloud Synchronization Systems", desc: "Scale data distribution loops reliably across devices using global cloud backends." },
    { icon: "📡", title: "Offline Storage Systems", desc: "Store processing logic locally so critical application processes operate without structural data connections." },
    { icon: "⚡", title: "App Performance Tuning", desc: "Optimize core rendering layers, reduce energy impacts, and cut initial payload weights." },
    { icon: "🛠️", title: "Store Management & Audits", desc: "Execute regular package patches, operational testing loops, and play/app store publishing management." }
  ];

  const technologies = {
    "Mobile Languages": ["Swift", "Kotlin", "Java", "Objective-C", "Dart", "JavaScript"],
    "Cross-Platform": ["React Native", "Flutter", "Expo"],
    "Backend & APIs": ["Node.js", "Express.js", "GraphQL", "REST APIs"],
    "Database Systems": ["SQLite", "Realm", "MongoDB", "PostgreSQL"],
    "Tools & Services": ["Firebase", "Git & GitHub", "AWS Mobile Services", "Docker", "CI/CD Pipelines"]
  };

  const processSteps = [
    { num: "01", title: "Concept Exploration", desc: "Defining clear project targets, target customer personas, and overall feature scopes." },
    { num: "02", title: "System Prototyping", desc: "Structuring user wireframes, flow routing loops, and interactive design mockups." },
    { num: "03", title: "Interface Production", desc: "Crafting beautiful graphical components optimized across multi-screen layouts." },
    { num: "04", title: "Core Architecture Build", desc: "Programming features natively or via cross-platform frameworks using optimized standards." },
    { num: "05", title: "Quality Verification", desc: "Executing automated build checks, system security inspections, and responsive layouts testing." },
    { num: "06", title: "App Store Packaging", desc: "Navigating App Store Review Guideline verifications and launching packages publicly." },
    { num: "07", title: "Lifecycle Management", desc: "Providing active feature patching, server tracking, and continuous software upkeep." }
  ];

  const industries = [
    "On-Demand Services", "Healthcare & Telemedicine", "E-Learning & Education", "Fintech & Banking",
    "Real Estate Tools", "E-Commerce & Retail", "Logistics & Fleet Tracking", "Social Networking",
    "Travel & Booking Systems", "Fitness & Wellness", "Corporate Operations", "Startups"
  ];

  const designBenefits = [
    "Fluid User Experiences", "Thumb-Optimized Layouts", "Dark Mode Ready Themes", 
    "Ultra-Fast Interface Responses", "Brand-Centric Elements", "Platform Style Alignment", 
    "Smooth Interactive States", "Multi-Device Fluidity"
  ];

  const whyChooseUs = [
    "Skilled Mobile App Engineering Team", "Custom Business Native Solutions", "Elegant Native UI/UX Implementations",
    "Highly Secure Coding Practices", "Optimized Cross-Platform Layouts", "Scalable Enterprise Solutions Engine",
    "Low Battery & Resource Impact", "Clear Milestone Tracking", "On-Time App Store Deployment",
    "Continuous Maintenance & Support", "Cutting-Edge Mobile Tech Stack", "100% Custom Product Satisfaction Focus"
  ];

  return (

    <> <Helmet>
  {/* Primary SEO */}
  <title>
    Mobile App Development Services | Android & iOS App Development | Affsoft IT Solutions Pvt. Ltd.
  </title>

  <meta
    name="title"
    content="Mobile App Development Services | Android & iOS App Development | Affsoft IT Solutions Pvt. Ltd."
  />

  <meta
    name="description"
    content="Affsoft IT Solutions Pvt. Ltd. provides professional mobile app development services, including Android app development, iOS app development, Flutter app development, React Native development, cross-platform apps, custom mobile applications, enterprise mobility solutions, UI/UX design, API integration, and scalable business apps."
  />

  <meta
    name="keywords"
    content="Mobile App Development, Android App Development, iOS App Development, Flutter App Development, React Native Development, Cross Platform App Development, Custom Mobile App Development, Hybrid App Development, Native App Development, Enterprise Mobile Apps, Mobile UI UX Design, App Maintenance, API Integration, Business Mobile Applications, Mobile Application Development Company, Mobile App Development Services, Affsoft IT Solutions Pvt. Ltd."
  />

  <meta name="robots" content="index, follow" />
  <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
  <meta name="language" content="English" />
  <meta name="theme-color" content="#0d5c53" />

  {/* Canonical */}
  <link
    rel="canonical"
    href="https://www.affsoft.com/mobile-app-development"
  />

  {/* Open Graph */}
  <meta property="og:type" content="website" />
  <meta
    property="og:site_name"
    content="Affsoft IT Solutions Pvt. Ltd."
  />
  <meta
    property="og:title"
    content="Professional Mobile App Development Services | Affsoft IT Solutions Pvt. Ltd."
  />
  <meta
    property="og:description"
    content="Develop powerful Android, iOS, Flutter, and React Native applications with Affsoft IT Solutions Pvt. Ltd. We build secure, scalable, and user-friendly mobile apps tailored to your business needs."
  />
  <meta
    property="og:url"
    content="https://www.affsoft.com/mobile-app-development"
  />
  <meta
    property="og:image"
    content="https://www.affsoft.com/images/affsoft-logo.png"
  />

  {/* Twitter */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta
    name="twitter:title"
    content="Mobile App Development Services | Android & iOS App Development | Affsoft IT Solutions Pvt. Ltd."
  />
  <meta
    name="twitter:description"
    content="Build innovative Android, iOS, Flutter, and React Native mobile applications with Affsoft IT Solutions Pvt. Ltd. We deliver custom, secure, and scalable mobile app solutions for startups and enterprises."
  />
  <meta
    name="twitter:image"
    content="https://www.affsoft.com/images/affsoft-logo.png"
  />
</Helmet>
    <div className="mobile-app-dev-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>
      
      {/* ================= HERO SECTION ================= */}
      <div 
        className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden" 
        style={{ 
          backgroundImage: "url('./images/home.jpg')",
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: '85vh',
          backgroundColor: '#0a0915' // dark fallback
        }}
      >
        {/* Background Grid Lines */}
        <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.15 }}>
          <div className="grid-line line-v-1"></div>
          <div className="grid-line line-v-2"></div>
          <div className="grid-line line-v-3"></div>
        </div>

        {/* Infinite Slow-Scrolling Marquee Text Background Wrapper */}
        <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in" style={{ top: '15%', opacity: 0.05 }}>
            
        </div>

        <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in">
          <div className="marquee-content text-white">
            <span>Mobile App &nbsp; and &nbsp;</span>
            <span>Development </span>
          </div>
        </div>

        {/* Main Content Layout */}
        <div className="container position-relative z-3 my-5">
          <div className="row align-items-center py-4">
            
            {/* Left Side Info */}
            <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
              <div className="asterisk-icon mb-3 text-warning fs-3">✳</div>
              <h1 className="fw-bold mb-3 display-5">Mobile App Development</h1>
              <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                At Affsoft IT Solutions Pvt. Ltd., we construct feature-rich, beautiful, and secure mobile applications designed to bring your operations straight to customers' pockets. Our engineering methodologies marry aesthetic precision with functional architecture, rendering native performance and seamless interfaces across platforms.
              </p>
              <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                <span>➔</span>
                <span>About Affsoft</span>
              </button>
            </div>

            {/* Center Section */}
            <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
              <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                <span className="cursor text-warning fs-4 animate-blink">|</span>
              </div>
            </div>

            {/* Right Section */}
            <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
              <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                  <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ================= MOBILE DESIGN SECTION ================= */}
      <section className="py-5 bg-white">
        <div className="container">
          <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
            <span className="badge bg-primary px-3 py-2 rounded-pill mb-2 text-uppercase">App UI/UX Design</span>
            <h2 className="fw-bold text-dark display-6">Stunning Visuals Paired with Seamless Layouts</h2>
            <p className="text-muted mt-3">
              An application's true potential is realized through effortless interface interactions. At Affsoft IT Solutions Pvt. Ltd., we outline custom mobile journeys built around modern interaction models to improve customer engagement and simplify navigation.
            </p>
          </div>

          <div className="row g-4">
            {designServices.map((service, index) => (
              <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                  <div className="fs-1 mb-3 text-primary">{service.icon}</div>
                  <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                  <p className="text-muted mb-0 small">{service.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Design Benefits Checklist */}
          <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
            <h5 className="fw-bold text-center text-dark mb-4">Why Choose Our Mobile UI/UX Architecture?</h5>
            <div className="row g-3">
              {designBenefits.map((benefit, index) => (
                <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                  <span className="text-success fs-5">✓</span>
                  <span className="text-secondary small fw-medium">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================= MOBILE DEVELOPMENT SECTION ================= */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
            <span className="badge bg-success px-3 py-2 rounded-pill mb-2 text-uppercase">Mobile Development</span>
            <h2 className="fw-bold text-dark display-6">Resilient, Secure & Native Platform Performance</h2>
            <p className="text-muted mt-3">
              We create production-grade mobile code utilizing powerful framework libraries. Whether deploying a specialized multi-device system or building custom cross-platform software from scratch, our software engineers build high-performance products tailored for growth.
            </p>
          </div>

          <div className="row g-4">
            {devServices.map((service, index) => (
              <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                  <div className="fs-1 mb-3 text-success">{service.icon}</div>
                  <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                  <p className="text-muted mb-0 small">{service.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= TECHNOLOGIES SECTION ================= */}
      <section className="py-5 bg-dark text-white">
        <div className="container">
          <div className="text-center mb-5 scroll-anim-fade-in-up">
            <span className="badge bg-warning text-dark px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold">Mobile Tech Stack</span>
            <h2 className="fw-bold display-6">Mobile Ecosystems We Master</h2>
            <p className="text-light-50">We structure future-ready mobile apps utilizing secure industry ecosystems.</p>
          </div>

          <div className="row g-4 justify-content-center">
            {Object.entries(technologies).map(([category, list], index) => (
              <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                  <h5 className="fw-bold text-warning mb-3 border-bottom border-secondary pb-2">{category}</h5>
                  <ul className="list-unstyled mb-0">
                    {list.map((tech, i) => (
                      <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                        <span className="text-warning opacity-75">•</span> {tech}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= DEVELOPMENT PROCESS ================= */}
      <section className="py-5 bg-white">
        <div className="container">
          <div className="text-center mb-5 scroll-anim-fade-in-up">
            <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Roadmap</span>
            <h2 className="fw-bold text-dark display-6">Our Lifecycle Engineering Flow</h2>
            <p className="text-muted">An agile blueprint detailing our step-by-step conversion of app models into store realities.</p>
          </div>

          <div className="row g-3">
            {processSteps.map((step, index) => (
              <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                <div className="p-4 rounded-4 bg-light border-start border-primary border-4 h-100 shadow-sm">
                  <div className="fw-bold text-primary display-6 mb-2">{step.num}</div>
                  <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                  <p className="text-muted small mb-0">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= INDUSTRIES SERVED ================= */}
      <section className="py-5 bg-light">
        <div className="container text-center">
          <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Industries We Serve</h2>
          <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
            {industries.map((industry, index) => (
              <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                🏢 {industry}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ================= WHY CHOOSE AFFSOFT ================= */}
      <section className="py-5 bg-white border-top">
        <div className="container">
          <div className="text-center mb-5 scroll-anim-fade-in-up">
            <h2 className="fw-bold text-dark display-6">Why Choose Affsoft IT Solutions Pvt. Ltd.?</h2>
            <p className="text-muted">We deliver highly responsive, optimized, and customer-centric application solutions.</p>
          </div>

          <div className="row g-3">
            {whyChooseUs.map((reason, index) => (
              <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                  <span className="text-primary fs-4">🛡️</span>
                  <span className="text-dark fw-semibold small">{reason}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Styles Injection */}
      <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

    </div>
    </>
  );
};

export default MobileAppDevelopment;